import React, { useEffect, useState } from 'react';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationSetSubProgramPreferenceProgressScurve } from 'api/mutations/setPrefferedSPIMethod.mutation';
import { useMutationSetSubProgramPreferencePartialCompletion } from 'api/mutations/setSubProgramPreferencePartialCompletion.mutation';

import {
    ProgressMeasurement,
    options as progressMeasurementOptions,
} from 'components/common/GlobalFilterComponent/components/progressMeasurementFilter/progressMeasurementFilter';

import { getDecodedValue } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramProgressOverview/programProgressOverview.utils';
import subProgramPreferencesClasses from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/SubProgramPreferences/components/subProgramPreferences.module.scss';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/SubProgramPreferences/progressMeasurementPreference/progressMeasurementPreference.module.scss';
import { useProgressMeasurement } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/SubProgramPreferences/progressMeasurementPreference/useProgressMeasurementPreference';
import { Select } from 'components/DesignSystem/Select';
import { ConfirmSaveModal } from 'components/common';

export const ProgressMeasurementPreference = () => {
    const { contract } = useProjectTreeStore();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<ISelectOption<ProgressMeasurement> | null>(null);
    const { handleSnackBar } = useCustomSnackBar();

    const { value: progressMeasurementValue, refetch } = useProgressMeasurement();

    const { mutateAsync: mutateProgressScurve, isLoading: mutateLoadingProgressScurve } =
        useMutationSetSubProgramPreferenceProgressScurve();
    const { mutateAsync: mutatePartialCompletion, isLoading: mutateLoadingPartialCompletion } =
        useMutationSetSubProgramPreferencePartialCompletion();

    useEffect(() => {
        if (progressMeasurementValue) {
            const value = progressMeasurementOptions.find((item) => item.value === progressMeasurementValue) || null;
            if (value) {
                setSelectedValue(value);
            }
        }
    }, [progressMeasurementValue]);

    const currentValue = progressMeasurementOptions.find((item) => item.value === progressMeasurementValue) || null;

    const handleChange = (value: ISelectOption<ProgressMeasurement> | null) => {
        setShowConfirmModal(true);
        setSelectedValue(value);
    };

    const handleHideModal = () => {
        setShowConfirmModal(false);
        setSelectedValue(currentValue);
    };

    const handleSave = () => {
        setShowConfirmModal(false);
        if (selectedValue && contract) {
            const value = getDecodedValue(selectedValue.value);
            Promise.all([
                mutateProgressScurve({
                    subProgramId: contract.id,
                    option: value.progressMeasurement,
                }),
                mutatePartialCompletion({
                    subProgramId: contract.id,
                    option: value.partialCompletionCredit,
                }),
            ])
                .then(() => {
                    handleSnackBar('Progress Measurement has changed', 'success');
                })
                .catch((error: QueryError) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                })
                .finally(() => {
                    refetch();
                });
        }
    };

    return (
        <div className={classes.progressMeasurementPreference}>
            <span className={subProgramPreferencesClasses.title}>Preferred Method:</span>
            <div>
                <Select<ISelectOption<ProgressMeasurement>>
                    value={selectedValue}
                    options={progressMeasurementOptions}
                    onChange={handleChange}
                    isClearable={false}
                />
            </div>
            <ConfirmSaveModal
                isShowModal={showConfirmModal}
                onHideModal={handleHideModal}
                onConfirm={handleSave}
                isLoading={mutateLoadingProgressScurve || mutateLoadingPartialCompletion}
            >
                You have made changes. Would you like to save them?
            </ConfirmSaveModal>
        </div>
    );
};
